import React, { useState } from 'react'
import { ChevronDownIcon, ChevronRightIcon, BriefcaseIcon, ClockIcon, MapPinIcon, UserIcon, CalendarIcon, UsersIcon } from 'lucide-react'
import { formatDateSafely } from '../utils/dateUtils'

interface ServiceData {
  type: string
  category?: string
  frequency?: string
  duration?: string
  group_size?: string
  location?: string
  provider?: string
  start_date?: string
  end_date?: string
  goals_addressed?: string[]
  service_description?: string
  source_page?: number
}

interface ServiceRendererProps {
  services: ServiceData[]
  onSourceClick?: (pageNumber: number) => void
}

const ServiceRenderer: React.FC<ServiceRendererProps> = ({
  services,
  onSourceClick
}) => {
  const [expandedServices, setExpandedServices] = useState<Set<number>>(new Set())

  const toggleService = (index: number) => {
    const newExpanded = new Set(expandedServices)
    if (newExpanded.has(index)) {
      newExpanded.delete(index)
    } else {
      newExpanded.add(index)
    }
    setExpandedServices(newExpanded)
  }

  const getServiceColor = (type?: string): string => {
    if (!type) return 'bg-gray-100 text-gray-700'
    
    const lowerType = type.toLowerCase()
    if (lowerType.includes('special education')) return 'bg-blue-100 text-blue-800'
    if (lowerType.includes('speech')) return 'bg-green-100 text-green-800'
    if (lowerType.includes('occupational') || lowerType.includes('ot')) return 'bg-purple-100 text-purple-800'
    if (lowerType.includes('physical') || lowerType.includes('pt')) return 'bg-orange-100 text-orange-800'
    if (lowerType.includes('counseling') || lowerType.includes('psychology')) return 'bg-pink-100 text-pink-800'
    if (lowerType.includes('transportation')) return 'bg-yellow-100 text-yellow-800'
    
    return 'bg-indigo-100 text-indigo-800'
  }

  const formatSchedule = (frequency?: string, duration?: string): string => {
    if (!frequency && !duration) return ''
    if (frequency && duration) return `${frequency} × ${duration}`
    return frequency || duration || ''
  }

  const formatDateRange = (startDate?: string, endDate?: string): string => {
    if (!startDate && !endDate) return ''
    if (startDate && endDate) {
      // Format dates safely without timezone issues
      const formatDate = (dateStr: string) => {
        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return formatDateSafely(dateStr, { format: 'short' }).replace(/,.*/, '') // Remove day for brief display
        }
        return dateStr
      }
      return `${formatDate(startDate)} - ${formatDate(endDate)}`
    }
    return startDate || endDate || ''
  }

  const renderSourceReference = (pageNumber: number) => {
    if (!onSourceClick) return null
    return (
      <button
        onClick={(e) => {
          e.stopPropagation()
          onSourceClick(pageNumber)
        }}
        className="ml-2 p-1 rounded hover:bg-gray-100 transition-colors text-gray-400 hover:text-blue-600"
        title={`Go to page ${pageNumber} in PDF`}
      >
        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
        </svg>
      </button>
    )
  }

  if (services.length === 0) {
    return (
      <div className="text-gray-500 italic p-4 bg-gray-50 rounded-lg">
        No services found
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {services.map((service, index) => {
        const isExpanded = expandedServices.has(index)
        const schedule = formatSchedule(service.frequency, service.duration)
        const dateRange = formatDateRange(service.start_date, service.end_date)
        
        return (
          <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
            {/* Header - Always Visible */}
            <div 
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => toggleService(index)}
            >
              <div className="flex-1 min-w-0">
                {/* Service Type and Category */}
                <div className="flex items-center mb-3">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getServiceColor(service.type)}`}>
                    <BriefcaseIcon className="h-4 w-4 mr-1.5" />
                    {service.type}
                  </span>
                  {service.category && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                      {service.category}
                    </span>
                  )}
                  {service.source_page && renderSourceReference(service.source_page)}
                </div>
                
                {/* Key Details in Two Rows */}
                <div className="space-y-2">
                  {/* First Row: Schedule, Location, Provider */}
                  <div className="flex items-center text-sm text-gray-600 space-x-6">
                    {schedule && (
                      <div className="flex items-center">
                        <ClockIcon className="h-4 w-4 mr-1" />
                        <span className="font-medium">{schedule}</span>
                      </div>
                    )}
                    
                    {service.location && (
                      <div className="flex items-center">
                        <MapPinIcon className="h-4 w-4 mr-1" />
                        <span>{service.location}</span>
                      </div>
                    )}
                    
                    {service.provider && (
                      <div className="flex items-center">
                        <UserIcon className="h-4 w-4 mr-1" />
                        <span>{service.provider}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Second Row: Group Size, Date Range, Goals */}
                  <div className="flex items-center text-sm text-gray-600 space-x-6">
                    {service.group_size && (
                      <div className="flex items-center">
                        <UsersIcon className="h-4 w-4 mr-1" />
                        <span>{service.group_size}</span>
                      </div>
                    )}
                    
                    {dateRange && (
                      <div className="flex items-center">
                        <CalendarIcon className="h-4 w-4 mr-1" />
                        <span>{dateRange}</span>
                      </div>
                    )}
                    
                    {service.goals_addressed && service.goals_addressed.length > 0 && (
                      <div className="flex items-center">
                        <span className="text-xs font-medium text-blue-600">
                          {service.goals_addressed.length} goal{service.goals_addressed.length !== 1 ? 's' : ''} addressed
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex-shrink-0 ml-4">
                {isExpanded ? (
                  <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                )}
              </div>
            </div>
            
            {/* Expanded Details */}
            {isExpanded && (
              <div className="border-t border-gray-100 px-4 pb-4">
                <div className="pt-4 space-y-4">
                  
                  {/* Service Description */}
                  {service.service_description && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Service Description</h4>
                      <p className="text-sm text-gray-900">{service.service_description}</p>
                    </div>
                  )}
                  
                  {/* Goals Addressed */}
                  {service.goals_addressed && service.goals_addressed.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Goals Addressed</h4>
                      <div className="flex flex-wrap gap-2">
                        {service.goals_addressed.map((goal, idx) => (
                          <span key={idx} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700">
                            {goal}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Service Details Grid */}
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <h5 className="font-medium text-gray-700 mb-1">Schedule Details</h5>
                      <div className="space-y-1 text-gray-900">
                        {service.frequency && <div>Frequency: {service.frequency}</div>}
                        {service.duration && <div>Duration: {service.duration}</div>}
                        {service.group_size && <div>Group Size: {service.group_size}</div>}
                      </div>
                    </div>
                    
                    <div>
                      <h5 className="font-medium text-gray-700 mb-1">Location & Provider</h5>
                      <div className="space-y-1 text-gray-900">
                        {service.location && <div>Location: {service.location}</div>}
                        {service.provider && <div>Provider: {service.provider}</div>}
                        {service.category && <div>Category: {service.category}</div>}
                      </div>
                    </div>
                    
                    <div>
                      <h5 className="font-medium text-gray-700 mb-1">Timeline</h5>
                      <div className="space-y-1 text-gray-900">
                        {service.start_date && <div>Start: {service.start_date}</div>}
                        {service.end_date && <div>End: {service.end_date}</div>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default ServiceRenderer
